import { SeatingTemplateTable } from 'state/templateInfo';
import { dashboard } from 'types';
import { first, isEmpty } from 'es-toolkit/compat';
import { isPresent } from '@tock/shared/utils/typeUtils';

export type ModeType = 'ADD' | 'REMOVE' | undefined;
export interface ExperienceConfiguration {
  ticketTypeId: string;
  mode: ModeType;
  effectiveAtMinutesBeforeService: string | undefined;
}

export function getExperienceConfigurationsFromTicketTypeConfigs(
  ticketTypeConfigs: dashboard.ISeatingTemplateTableTicketTypeConfig[]
): ExperienceConfiguration[] {
  return (
    ticketTypeConfigs
      ?.filter(
        (ticketTypeConfig) =>
          // for the scope of this feature, we only are working with a single on/off experience optimization.
          // More than 2 conditional ranges would be advanced and out of scope for the feature, 1 conditional
          // range means no optimizations are applied to display.
          (ticketTypeConfig.conditionalRanges?.length || 0) === 2
      )
      .map((ticketTypeConfig) => {
        const { conditionalRanges } = ticketTypeConfig;

        const effectiveAtMinutesBefore = first(
          conditionalRanges
            ?.map((conditionalRange) => conditionalRange.condition?.effectiveAtMinutesBefore)
            .filter(isPresent)
        );

        // initial range is defined by having no condition
        const initialRange = conditionalRanges?.find(
          (conditionalRange) => conditionalRange.condition == null
        );

        const initialBookableRanges = initialRange?.bookableRanges?.bookableRange;
        // initial state is on if the initial range is undefined or the initial range's bookable range is not empty
        const initialStateIsOn = initialBookableRanges == null || !isEmpty(initialBookableRanges);

        return {
          ticketTypeId: String(ticketTypeConfig.ticketTypeId || 0),
          mode: initialStateIsOn ? 'REMOVE' : 'ADD',
          effectiveAtMinutesBeforeService: String(effectiveAtMinutesBefore || 0),
        };
      }) || []
  );
}

export const dynamicOptimizationTimeOptionsByMinutes = {
  [10 * 7 * 24 * 60]: '10 weeks',
  [9 * 7 * 24 * 60]: '9 weeks',
  [8 * 7 * 24 * 60]: '8 weeks',
  [7 * 7 * 24 * 60]: '7 weeks',
  [6 * 7 * 24 * 60]: '6 weeks',
  [5 * 7 * 24 * 60]: '5 weeks',
  [4 * 7 * 24 * 60]: '4 weeks',
  [3 * 7 * 24 * 60]: '3 weeks',
  [2 * 7 * 24 * 60]: '2 weeks',
  [1 * 7 * 24 * 60]: '1 week',
  [6 * 24 * 60]: '6 days',
  [5 * 24 * 60]: '5 days',
  [4 * 24 * 60]: '4 days',
  [3 * 24 * 60]: '3 days',
  [2 * 24 * 60]: '2 days',
  [24 * 60]: '24 hours',
  [12 * 60]: '12 hours',
  [6 * 60]: '6 hours',
  [4 * 60]: '4 hours',
  [2 * 60]: '2 hours',
  [1 * 60]: '1 hour',
};

/**
 * These are not times that we want to display as options for newly created optimizations. However,
 * online availability optimizations can currently be set to these times, so we need to account for their display
 * for backwards compatibility.
 */
export const dynamicOptimizationAdditionalTimeOptionsByMinutes = {
  [11 * 60]: '11 hours',
  [10 * 60]: '10 hours',
  [9 * 60]: '9 hours',
  [8 * 60]: '8 hours',
  [7 * 60]: '7 hours',
  [5 * 60]: '5 hours',
  [3 * 60]: '3 hour',
};

export const getFormattedDynamicOptimizationTimeBeforeService = (time: number) => {
  return (
    dynamicOptimizationTimeOptionsByMinutes[time] ??
    dynamicOptimizationAdditionalTimeOptionsByMinutes[time]
  );
};

export type TableOptimizationsForExperience = {
  tableIdsWithAddOptimization: number[];
  tableIdsWithRemoveOptimization: number[];
};

export type TableOptimizationsByTimeAndExperience = Map<
  number /** Represents the effectiveAtMinutesBefore value */,
  Map<number, TableOptimizationsForExperience>
>;

export function computeTableOptimizationsForSeatingTemplateTables(
  seatingTemplateTables: dashboard.ISeatingTemplateTable[]
) {
  const tableOptimizationsByTimeAndExperience: TableOptimizationsByTimeAndExperience = new Map();
  seatingTemplateTables.forEach((seatingTemplateTable) => {
    accumulateOptimizationsOnTable(tableOptimizationsByTimeAndExperience, seatingTemplateTable);
  });

  return tableOptimizationsByTimeAndExperience;
}

function accumulateOptimizationsOnTable(
  tableOptimizationsByTimeAndExperience: TableOptimizationsByTimeAndExperience,
  seatingTemplateTable: SeatingTemplateTable | dashboard.ISeatingTemplateTable
) {
  seatingTemplateTable.ticketTypeConfig?.forEach((config) => {
    (config.conditionalRanges ?? []).forEach((range) => {
      if (!config.ticketTypeId) {
        // Short circuit / guard
        return;
      }

      accumulateOptimizationsForExperienceOnTable(
        tableOptimizationsByTimeAndExperience,
        Number(seatingTemplateTable.tableId),
        config.ticketTypeId,
        range
      );
    });
  });
}

function accumulateOptimizationsForExperienceOnTable(
  tableOptimizationsByTimeAndExperience: TableOptimizationsByTimeAndExperience,
  tableId: number,
  ticketTypeId: number,
  range: dashboard.ISeatingTemplateConditionalRange
) {
  // Early return if the range doesn't have an effectiveAt time (i.e., if the range is not related to an optimization)
  if (!range.condition?.effectiveAtMinutesBefore) {
    return;
  }

  const optimizationType = getHasEmptyBookableRanges(range.bookableRanges?.bookableRange)
    ? 'REMOVE'
    : 'ADD';

  accumulateSingleOptimization(
    tableOptimizationsByTimeAndExperience,
    range.condition?.effectiveAtMinutesBefore ?? 0,
    ticketTypeId,
    tableId,
    optimizationType
  );
}

export function getHasEmptyBookableRanges(
  bookableRanges: dashboard.ISeatingTemplateTableExperienceBookableRange[] | undefined | null
) {
  return bookableRanges?.length === 0;
}

function accumulateSingleOptimization(
  tableOptimizationsByTimeAndExperience: TableOptimizationsByTimeAndExperience,
  effectiveAtMinutesBefore: number,
  ticketTypeId: number,
  tableId: number,
  optimizationType: 'ADD' | 'REMOVE'
) {
  const tableOptimizationsByExperience =
    tableOptimizationsByTimeAndExperience.get(effectiveAtMinutesBefore) ?? new Map();

  const existingTicketActionMap: TableOptimizationsForExperience =
    tableOptimizationsByExperience.get(ticketTypeId) ?? {
      tableIdsWithAddOptimization: [],
      tableIdsWithRemoveOptimization: [],
    };

  const optimizationTypeKey =
    optimizationType === 'ADD' ? 'tableIdsWithAddOptimization' : 'tableIdsWithRemoveOptimization';

  if (!existingTicketActionMap[optimizationTypeKey].includes(tableId)) {
    const updatedTables = [...existingTicketActionMap[optimizationTypeKey], tableId];
    const newTicketActionMap = {
      ...existingTicketActionMap,
      [optimizationTypeKey]: updatedTables,
    };

    const newTableOptimizationsByExperience = new Map(tableOptimizationsByExperience).set(
      ticketTypeId,
      newTicketActionMap
    );
    tableOptimizationsByTimeAndExperience.set(
      effectiveAtMinutesBefore,
      newTableOptimizationsByExperience
    );
  }
}

export const getOptimizationsSummary = (seatingTemplate: dashboard.ISeatingTemplate) => {
  const {
    tableAutoReleaseMinutes,
    table: seatingTemplateTables,
    isExperienceOptimizationEnabled,
    isTableSizingOptimizationEnabled,
  } = seatingTemplate;

  const tableAvailabilityOptimizationEnabled = !!tableAutoReleaseMinutes;

  const tableAvailabilityNumTablesOptimizedTotal =
    seatingTemplateTables?.filter((table) => table.autoReleaseEnabled).length ?? 0;
  const tableAvailabilityTimeReleasePriorToService = tableAutoReleaseMinutes
    ? `${tableAutoReleaseMinutes / 60} hours`
    : '--';

  const experiencesNumTablesOptimizedTotal = getUniqueTableIds(
    computeTableOptimizationsForSeatingTemplateTables(seatingTemplateTables ?? [])
  ).length;

  const tableSizingNumTablesOptimizedTotal = getUniqueTableSizingTableIds(
    computeTableSizingOptimizationsForSeatingTemplateTables(seatingTemplateTables ?? [])
  ).length;

  return {
    tableAvailabilityNumTablesOptimized: tableAvailabilityOptimizationEnabled
      ? tableAvailabilityNumTablesOptimizedTotal
      : 0,
    tableAvailabilityTimeReleasePriorToService,
    tableAvailabilityOptimizationEnabled,
    experiencesNumTablesOptimized: isExperienceOptimizationEnabled
      ? experiencesNumTablesOptimizedTotal
      : 0,
    tableSizingNumTablesOptimized: isTableSizingOptimizationEnabled
      ? tableSizingNumTablesOptimizedTotal
      : 0,

    experienceOptimizationEnabled: isExperienceOptimizationEnabled,
    tableSizingOptimizationEnabled: isTableSizingOptimizationEnabled,
  };
};

export const getUniqueTableIds = (
  optimizationsByTimeAndExperience: TableOptimizationsByTimeAndExperience
) => {
  const uniqueTableIds = new Set();

  optimizationsByTimeAndExperience.forEach((experienceMap) => {
    experienceMap.forEach((optimizations) => {
      optimizations.tableIdsWithAddOptimization.forEach((tableId) => {
        uniqueTableIds.add(tableId);
      });
      optimizations.tableIdsWithRemoveOptimization.forEach((tableId) => {
        uniqueTableIds.add(tableId);
      });
    });
  });

  return Array.from(uniqueTableIds);
};

export type TableSizingOptimizationsByTimeAndMinTableSize = Map<
  number /** Represents the effectiveAtMinBefore value */,
  Map<number, number[]> /** The key represents minPurchaseSize and the value represents tableIds */
>;

export const computeTableSizingOptimizationsForSeatingTemplateTables = (
  seatingTemplateTables: dashboard.ISeatingTemplateTable[]
) => {
  const tableSizingOptimizationsByTimeAndMinTableSize: TableSizingOptimizationsByTimeAndMinTableSize =
    new Map();

  seatingTemplateTables.forEach((seatingTemplateTable) => {
    seatingTemplateTable.tableSizeConfig?.forEach((config) => {
      accumulateTableSizingOptimizationsOnTable(
        tableSizingOptimizationsByTimeAndMinTableSize,
        seatingTemplateTable,
        config.effectiveAtMinutesBefore ?? 0,
        config.minPurchaseSize ?? 0
      );
    });
  });

  const tableSizingOptimizationSortedByTime = Array.from(
    tableSizingOptimizationsByTimeAndMinTableSize.entries()
  ).sort(([key1], [key2]) => key1 - key2);

  return new Map(tableSizingOptimizationSortedByTime);
};

function accumulateTableSizingOptimizationsOnTable(
  tableSizingOptimizationsByTimeAndMinTableSize: TableSizingOptimizationsByTimeAndMinTableSize,
  seatingTemplateTable: SeatingTemplateTable | dashboard.ISeatingTemplateTable,
  effectiveAtMinutesBefore: number,
  minPartySize: number
) {
  if (!effectiveAtMinutesBefore || !minPartySize || !seatingTemplateTable.tableId) {
    // Short circuit / guard
    return;
  }

  const tableSizingOptimizationsMap: Map<number, number[]> =
    tableSizingOptimizationsByTimeAndMinTableSize.get(effectiveAtMinutesBefore) ?? new Map();

  const existingTableIds = tableSizingOptimizationsMap.get(minPartySize) ?? [];

  if (!tableSizingOptimizationsMap.get(minPartySize)?.includes(seatingTemplateTable.tableId)) {
    const updatedTables = [...existingTableIds, seatingTemplateTable.tableId];

    const updatedEntry = tableSizingOptimizationsMap.set(minPartySize, updatedTables);

    tableSizingOptimizationsByTimeAndMinTableSize.set(effectiveAtMinutesBefore, updatedEntry);
  }
}

export const getUniqueTableSizingTableIds = (
  optimizationsByTimeAndTableSize: TableSizingOptimizationsByTimeAndMinTableSize
) => {
  const uniqueTableIds = new Set<number>();

  for (const innerMap of optimizationsByTimeAndTableSize.values()) {
    for (const tableIds of innerMap.values()) {
      tableIds.forEach((tableId) => uniqueTableIds.add(tableId));
    }
  }

  return Array.from(uniqueTableIds);
};

/**
 * Get the minimum table size, taking into account table size optimizations at the currently selected time before start of service
 */
export function getMinPurchaseSizeAtTimeBeforeService(
  tableSizeConfigs: dashboard.ISeatingTemplateTableSize[],
  timeBeforeServiceMinutes: number | null,
  defaultSize: number
): number {
  const sortedConfigs = [...tableSizeConfigs].sort((a, b) => {
    const aTimeBeforeServiceMinutes = a.effectiveAtMinutesBefore ?? Number.MAX_SAFE_INTEGER;
    const bTimeBeforeServiceMinutes = b.effectiveAtMinutesBefore ?? Number.MAX_SAFE_INTEGER;
    return aTimeBeforeServiceMinutes - bTimeBeforeServiceMinutes;
  });

  const matchingConfig = sortedConfigs.find(
    (config) =>
      config.effectiveAtMinutesBefore != null &&
      config.effectiveAtMinutesBefore >= (timeBeforeServiceMinutes ?? Number.MAX_SAFE_INTEGER)
  );

  return matchingConfig?.minPurchaseSize ?? defaultSize;
}
